import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetSentimentsBreakdownMutation } from "../../../store/api/lumi/sentiment";
import { safeDivide } from "../../../utils/safeDivide";
import { debounce } from "lodash";
import Scrollbars from "react-custom-scrollbars-2";
import { getComparingDates } from "../../../utils/getCompareDates";
import { ArrowGreen } from "../../../icons/ArrowGreen";
import { ArrowRed } from "../../../icons/ArrowRed";
import LargestSentimentMovementBar from "../SentimentBiggestChangePercentWidget/LargestSentimentMovementBar";
import NetSentimentChangeSummary from "./NetSentimentChangeSummary";
import { useSelector } from "react-redux";

// Import local CSS modules
import classes from "../SentimentBiggestChangePercentWidget/SentimentChangePercent.module.css";
import styles from "./NetSentiment.module.css";
// Import common grid classes
import commonStyles from "./CommonWidget.module.css";

const NetSentimentChangeWidget = (props) => {
  const {
    selectedDateName,
    interval,
    id,
    filters,
    setFilterDocCount,
    includeExamples,
    selectedPageSize,
    seletctedType,
  } = props;

  const [loading, setLoading] = useState(false);
  const [sentimentChanges, setSentimentChanges] = useState([]);
  const [comparingDates, setComparingDates] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [filterBaseline, setFilterBaseline] = useState(null);
  const [filterComparison, setFilterComparison] = useState(null);

  const sentiment_share_ai = useSelector((state) => state.auth.flags.sentiment_share_ai);
  const [searchParams] = useSearchParams();
  const isNewApp = searchParams.get("app") === "new";

  const [getSentimentsBreakdown] = useGetSentimentsBreakdownMutation();
  const conceptFilter = useMemo(() => JSON.stringify(getConceptFilter({ ...props, selectedPageSize })), [props, selectedPageSize]);

  const getSentimentData = useCallback(async () => {
    setLoading(true);
    const { data } = await getSentimentsBreakdown({
      id,
      concept_selector: JSON.parse(conceptFilter),
      filter: filters,
      include_examples: includeExamples,
      breakdowns: [{ name: selectedDateName, interval }],
    });
    setFilterDocCount(data.filter_count);
    const breakdownData = data.breakdowns[0].buckets;
    if (!breakdownData.length) {
      setSentimentChanges([]);
      setLoading(false);
      return;
    }
    const lastIndex = breakdownData.length - 1;
    const preLastIndex = breakdownData.length - 2;
    const lastData = breakdownData[lastIndex];
    const preLastData = breakdownData[preLastIndex];

    if (breakdownData.length > 1) {
      setFilterBaseline([{ name: selectedDateName, minimum: preLastData.label, maximum: lastData.label }]);
      setFilterComparison([{ name: selectedDateName, minimum: lastData.label }]);
    } else {
      setFilterBaseline([{ name: selectedDateName, minimum: lastData.label }]);
      setFilterComparison([{ name: selectedDateName, minimum: lastData.label }]);
    }

    const calculatedComparingDates = preLastData
      ? getComparingDates(preLastData, lastData, interval)
      : getComparingDates(lastData, lastData, interval);
    setComparingDates(calculatedComparingDates);

    const updatedSentiments = lastData.match_counts.map((sentiment) => {
      const preLastSentiment = preLastData?.match_counts.find((c) => c.name === sentiment.name);
      const currentNet = sentiment.sentiment_share.positive - sentiment.sentiment_share.negative;
      const previousNet = preLastSentiment
        ? preLastSentiment.sentiment_share.positive - preLastSentiment.sentiment_share.negative
        : 0;
      let changePercentage = (currentNet - previousNet) * 100;
      if (!preLastData) {
        changePercentage = currentNet * 100;
      }
      return {
        ...sentiment,
        preLastSentiment: preLastSentiment?.sentiment_share || { negative: 0, neutral: 0, positive: 0 },
        changePercentage,
      };
    });
    const changedConceptsPositive = [...updatedSentiments].sort(
      (a, b) => Math.abs(b.changePercentage) - Math.abs(a.changePercentage)
    );
    setSentimentChanges(changedConceptsPositive);
    setSelectedConcept(changedConceptsPositive[0] || null);
    setLoading(false);
  }, [getSentimentsBreakdown, id, conceptFilter, filters, includeExamples, selectedDateName, interval, setFilterDocCount]);

  useEffect(() => {
    const debouncedGet = debounce(() => getSentimentData(), 400);
    debouncedGet();
    return () => debouncedGet.cancel();
  }, [getSentimentData]);

  const height = props.widget.layout.h * 39 - 150;
  const EqualCompareDate = comparingDates[0] === comparingDates[1];

  if (loading) {
    return (
      <Spin
        className="spinner-by-center"
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      />
    );
  }

  // --- NEW UI ---
  if (isNewApp) {
    return (
      <div className={styles.widget_container}>
        <div className={styles.left_panel}>
          <div className={classes.visualization}>
            {/* Header using four-column grid */}
            <div className={`${commonStyles.fourColumnGrid} ${commonStyles.gridHeader}`}>
              <div className={commonStyles.gridItem}>Concept</div>
              <div className={commonStyles.gridItem}>Change</div>
              <div className={commonStyles.gridItem}></div>
              <div className={commonStyles.gridItem}>Movement</div>
            </div>
            <Scrollbars style={{ height }}>
              <div className={commonStyles.gridContainer}>
                {sentimentChanges.map((concept, index) => (
                  <div key={concept.name + index} className={`${commonStyles.fourColumnGrid} ${commonStyles.gridRow}`}>
                    <Tooltip title={concept.name}>
                      <div
                        className={commonStyles.gridItem}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedConcept(concept)}
                      >
                        {concept.name}
                      </div>
                    </Tooltip>
                    <div className={commonStyles.gridItem}>
                      {!EqualCompareDate &&
                        (concept.changePercentage >= 0 ? <ArrowGreen /> : <ArrowRed />)}
                      <span style={{ fontSize: "1.1rem", marginLeft: 4 }}>
                        {EqualCompareDate ? "0" : Math.abs(concept.changePercentage).toFixed(1)}%
                      </span>
                    </div>
                    <div className={commonStyles.gridItem}>
                      <div className={classes.sentimentLastDate}>{comparingDates[0]}</div>
                      {!EqualCompareDate && (
                        <div className={classes.sentimentPreLastDate}>{comparingDates[1]}</div>
                      )}
                    </div>
                    <div className={commonStyles.gridItem}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <LargestSentimentMovementBar concept={concept.sentiment_share} />
                        {!EqualCompareDate && (
                          <LargestSentimentMovementBar concept={concept.preLastSentiment} last />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Scrollbars>
          </div>
          <div className="sentiment-container-color">
            <div className="sentiment positive">
              <div className="color-box"></div>
              Positive
            </div>
            <div className="sentiment neutral">
              <div className="color-box"></div>
              Neutral
            </div>
            <div className="sentiment negative">
              <div className="color-box"></div>
              Negative
            </div>
          </div>
        </div>
        <div className={styles.right_panel}>
          <div className={styles.summary}>
            {sentiment_share_ai && selectedConcept && (
              <NetSentimentChangeSummary
                id={id}
                selectedConcept={selectedConcept}
                filterBaseline={filterBaseline}
                filterComparison={filterComparison}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- OLD UI ---
  return (
    <div className={classes.sentimentContainer}>
      <div className="sentiment-container">
        <div className="sentiment positive">
          <div className="color-box"></div>
          Positive
        </div>
        <div className="sentiment neutral">
          <div className="color-box"></div>
          Neutral
        </div>
        <div className="sentiment negative">
          <div className="color-box"></div>
          Negative
        </div>
      </div>
      <div className={classes.change_types_container}>
        <div className={classes.change_type_container}>
          <div className={classes.change_type_items}>
            <div className={`${commonStyles.fourColumnGrid} ${commonStyles.gridHeader}`}>
              <div className={commonStyles.gridItem}>Concept</div>
              <div className={commonStyles.gridItem}>Change</div>
              <div className={commonStyles.gridItem}>Date</div>
              <div className={commonStyles.gridItem}>Sentiment</div>
            </div>
            <Scrollbars style={{ height }}>
              <div className={commonStyles.gridContainer}>
                {sentimentChanges.map((concept, index) => (
                  <div key={concept.name + index} className={`${commonStyles.fourColumnGrid} ${commonStyles.gridRow}`}>
                    <Tooltip title={concept.name}>
                      <div
                        className={commonStyles.gridItem}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedConcept(concept)}
                      >
                        {concept.name}
                      </div>
                    </Tooltip>
                    <div className={commonStyles.gridItem}>
                      {!EqualCompareDate &&
                        (concept.changePercentage >= 0 ? <ArrowGreen /> : <ArrowRed />)}
                      <span style={{ fontSize: "1.1rem", marginLeft: 4 }}>
                        {EqualCompareDate ? "0" : Math.abs(concept.changePercentage).toFixed(1)}%
                      </span>
                    </div>
                    <div className={commonStyles.gridItem}>
                      <div className={classes.sentimentLastDate}>{comparingDates[0]}</div>
                      {!EqualCompareDate && (
                        <div className={classes.sentimentPreLastDate}>{comparingDates[1]}</div>
                      )}
                    </div>
                    <div className={commonStyles.gridItem}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <LargestSentimentMovementBar concept={concept.sentiment_share} />
                        {!EqualCompareDate && (
                          <LargestSentimentMovementBar concept={concept.preLastSentiment} last />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {sentiment_share_ai && selectedConcept && (
                <NetSentimentChangeSummary
                  id={id}
                  selectedConcept={selectedConcept}
                  filterBaseline={filterBaseline}
                  filterComparison={filterComparison}
                />
              )}
            </Scrollbars>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetSentimentChangeWidget;
